import { Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  Map,
  MessageSquare,
  Route as RouteIcon,
  Share2,
  Sparkles,
  Users,
  Wand2,
} from "lucide-react"
import { motion } from "motion/react"
import { Button } from "@/components/ui/button"
import { trackEvent } from "@/components/Analytics"

// The page exists to rank for "ai trip planner" (and the long tail around it),
// so the copy leans on the exact phrase in the H1/H2s while the substance is
// the positioning split: AI = the wedge (fast draft), the shared live map =
// what a chat AI can't be. Every claim maps to a real feature.

const steps = [
  {
    number: `01`,
    icon: MessageSquare,
    title: `Tell the AI where you're going`,
    body: `"5 days in Kyoto in November, slow mornings, lots of food." The AI trip planner drafts a full day-by-day itinerary in seconds — real places, in a sensible order.`,
  },
  {
    number: `02`,
    icon: Map,
    title: `Watch it land on a live map`,
    body: `Every stop is pinned and routed, so you can see that two places are an hour apart before the trip — not while standing on the wrong side of town.`,
  },
  {
    number: `03`,
    icon: Users,
    title: `Invite your crew and shape it together`,
    body: `Everyone edits the same living itinerary in real time. Drag days around, swap places, leave notes — no more screenshots of a chat thread.`,
  },
]

const features = [
  {
    icon: Wand2,
    title: `AI itinerary generator`,
    body: `Describe the trip in plain words and get a structured, editable plan — not a wall of text to copy-paste.`,
  },
  {
    icon: Map,
    title: `Everything on one live map`,
    body: `Geocoded pins, day-by-day routes, and travel times between stops, updating as the plan changes.`,
  },
  {
    icon: CalendarDays,
    title: `Drag-and-drop days`,
    body: `Move day 4 to day 2 with your hands. Reorder stops, shift whole days, and the map keeps up.`,
  },
  {
    icon: Users,
    title: `Plan together, live`,
    body: `Invite your travel group and edit the same itinerary at the same time — changes appear instantly for everyone.`,
  },
  {
    icon: RouteIcon,
    title: `Smart routing between stops`,
    body: `See distances and travel modes between places, so each day actually fits in a day.`,
  },
  {
    icon: Share2,
    title: `Share it or publish it`,
    body: `Send a read-only link to family, or publish the finished plan as a guide other travelers can copy.`,
  },
]

const faqs = [
  {
    q: `Is ChicTravel's AI trip planner free?`,
    a: `Yes — creating trips, generating AI itineraries, the live map, and collaborating with your group are all free. Pro adds extras like ChicReview, PDF export, and calendar export.`,
  },
  {
    q: `How is this different from asking ChatGPT or Claude to plan my trip?`,
    a: `A chat AI gives you text, and the plan dies when the conversation scrolls away. ChicTravel gives the same AI draft a place to live: every stop is pinned on a live map, days are drag-and-drop, and your whole group can edit the plan together in real time.`,
  },
  {
    q: `Can the AI plan a multi-city or multi-week trip?`,
    a: `Yes. Describe the whole route — "two weeks in Japan: Tokyo, Kyoto, Osaka" — and the planner drafts each city day by day. You can regenerate any day or rearrange cities afterwards.`,
  },
  {
    q: `Can my friends edit the trip without an account?`,
    a: `Anyone with the share link can view the full itinerary and map. To edit together, collaborators create a free account so every change is saved and synced live.`,
  },
  {
    q: `Does it work on my phone during the trip?`,
    a: `Yes — your itinerary, map, and notes are with you on the road, and edits sync across everyone's devices as plans change mid-trip.`,
  },
]

function ctaClick(cta: string) {
  trackEvent(`cta_click`, { page: `ai-trip-planner`, cta })
}

export function AiTripPlannerPage() {
  return (
    <main className="min-h-screen bg-background">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-12 md:pt-16 md:pb-16">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center gap-5">
          <motion.div
            className="contents"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 w-fit rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Free to start
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight">
              The AI trip planner that puts
              <br />
              <span className="text-primary">your whole trip on one map.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl">
              Chic, your AI copilot, drafts a day-by-day starting point in
              seconds. Every place lands on a live map. Your whole crew edits
              it together, in real time — so the plan stays yours and doesn't
              die in a chat window.
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Button
                size="lg"
                className="rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2"
                asChild
              >
                <Link to="/sign-up" onClick={() => ctaClick(`hero`)}>
                  Plan a trip free <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-12 text-base font-medium"
                asChild
              >
                <Link to="/trips/explore" onClick={() => ctaClick(`hero-explore`)}>
                  Browse real itineraries
                </Link>
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">No credit card required</p>
          </motion.div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              From a blank page to a shared plan in minutes
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={s.number}
                className="rounded-2xl border border-border bg-background p-6 flex flex-col gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-black text-primary/20 leading-none">
                    {s.number}
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </div>
                </div>
                <h3 className="font-bold text-lg">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why not just a chatbot ────────────────────────────────────── */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            Why not just use a chatbot?
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-5">
            An AI trip planner is more than an AI that talks about trips
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            ChatGPT and Claude write great trip ideas — and then the plan is
            stuck in a chat. No map, so you can't see that Tuesday zig-zags
            across the city. No structure, so reordering a day means re-prompting
            and re-reading. And your travel partners can't touch any of it.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            ChicTravel uses AI for what it's best at — the fast first draft —
            and gives it the three things text can't have:{` `}
            <span className="font-semibold text-foreground">geography</span> (a
            live map),{` `}
            <span className="font-semibold text-foreground">structure</span>{` `}
            (drag-and-drop days), and{` `}
            <span className="font-semibold text-foreground">people</span> (your
            whole group, editing the same plan, live).
          </p>
        </div>
      </section>

      {/* ── Feature grid ──────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              What's inside
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Everything a trip planner needs, in one place
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                className="rounded-2xl border border-border bg-background p-6 flex gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              AI trip planner questions, answered
            </h2>
          </div>
          <div className="flex flex-col gap-6">
            {faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <h3 className="font-bold text-base mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-primary">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-primary-foreground mb-4">
            Plan your next trip with AI — together
          </h2>
          <p className="text-primary-foreground/80 text-base mb-10">
            Start with an AI draft, end with a living itinerary your whole crew
            built on one map.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 h-12 font-semibold shadow-md gap-2 bg-white text-primary hover:bg-white/90"
              asChild
            >
              <Link to="/sign-up" onClick={() => ctaClick(`footer`)}>
                Start planning free <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="text-primary-foreground/60 text-xs mt-4">
            No credit card required
          </p>
        </motion.div>
      </section>
    </main>
  )
}
